<template>
  <language-setting :show-language-setting="hasConnection" :selectedLanguage="selectedLanguage" :languages="languages"
    @language-selected="onLanguageSelected" />
  <send-mail-button v-if="isSharing" :roomId="roomId"/>
  <div class="live-speaker">
    <div class="language-container" :class="{ 'selected': isLanguageSelected }" v-show="!hasConnection">
      <language-dropdown v-model="selectedLanguage" :value="selectedLanguage" :languages="languages"
        :disabled="isConnecting" @change="reconnectWebSocket" />
      <connect-button :is-connecting="isConnecting" :is-connected="isConnected" :connect-failed="connectFailed"
        :socket-closed="socketClosed" :is-language-changed="isLanguageChanged"
        :disabled="isConnecting || !selectedLanguage" @click="connectWebSocket" />
    </div>
    <messages-list v-if="hasConnection" :messages="messages" ref="messageContainer"
      :class="{ 'connected': hasConnection }" />
    <error-handler-dialog ref="errorDialog" @reconnect="reconnectWebSocketFromDialog" @cancel="closeDialog"
      @reload="reloadPage" />
  </div>
</template>

<script>
import { LANGUAGES_TRANSLATE } from '@/constants/language.js';
import { WSS_BASE_URL } from '@/constants/value.js';
import LanguageDropdown from '@/components/LanguageDropdown.vue';
import ConnectButton from '@/components/ConnectButton.vue';
import MessagesList from '@/components/MessagesList.vue';
import ErrorHandlerDialog from '@/components/ErrorHandlerDialog.vue';
import LanguageSetting from '@/components/LanguageSetting.vue';
import SendMailButton from '@/components/SendMailButton.vue';
import { useWakeLock } from '@vueuse/core';
import { reactive } from "vue";
import { useRoomInfo } from '@/services/roomInfoService';

export default {
  name: 'LiveSpeaker',
  components: {
    LanguageDropdown,
    ConnectButton,
    MessagesList,
    ErrorHandlerDialog,
    LanguageSetting,
    SendMailButton
  },
  data() {
    return {
      selectedLanguage: '',
      isLanguageSelected: false,
      isConnecting: false,
      isConnected: false,
      connectFailed: false,
      socketClosed: true,
      isLanguageChanged: false,
      isManualClose: false,
      hasConnection: false,
      isSharing: false,
      socket: null,
      messages: [],
      languages: LANGUAGES_TRANSLATE
    };
  },
  computed: {
    roomId() {
      return this.$route.params.roomid;
    }
  },
  methods: {
    async getRoomInfo() {
      try {
        const roomInfo = await useRoomInfo({ roomId: this.roomId });
        this.isSharing = roomInfo.is_sharing || false;
      } catch (error) {
        this.isSharing = false;
      }
    },
    connectWebSocket() {
      if (!this.selectedLanguage || !this.roomId || this.isConnecting) {
        return;
      }

      this.isConnecting = true;
      this.isLanguageSelected = true;
      this.connectFailed = false;
      this.socketClosed = false;
      this.isLanguageChanged = false;

      const wsUrl = `${WSS_BASE_URL}/${this.roomId}/listener/${this.selectedLanguage.toUpperCase()}`;
      console.log('Connecting to:', wsUrl);

      this.socket = new WebSocket(wsUrl);
      const wakeLock = reactive(useWakeLock());
      console.log("Is wake lock supported?", wakeLock.isSupported);
      wakeLock.request('screen');

      this.socket.onopen = () => {
        console.log('WebSocket connected successfully');
        this.isConnecting = false;
        this.isConnected = true;
        this.hasConnection = true;
      };

      this.socket.onmessage = (event) => {
        let message = this.decodeUnicode(event.data);
        this.messages.push(message);
        this.scrollToLatestMessage();
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.connectFailed = true;
        this.isConnected = false;
        this.$refs.errorDialog.showModal('error');
      };

      this.socket.onclose = () => {
        this.isConnecting = false;
        this.isConnected = false;
        this.socketClosed = true;
        if (!this.isManualClose) {
          this.$refs.errorDialog.showModal('close');
        }
        this.isManualClose = false;
      };
    },
    onLanguageSelected(langCode) {
      this.selectedLanguage = langCode;
      if (this.socket && this.isConnected) {
        this.isManualClose = true;
        this.socket.close();
      }
      this.connectWebSocket();
    },
    reconnectWebSocket() {
      if (this.socket && this.isConnected) {
        this.socket.close();
        this.isLanguageChanged = true;
      }
    },
    reconnectWebSocketFromDialog() {
      this.$refs.errorDialog.close();
      this.connectWebSocket();
    },
    reloadPage() {
      this.$refs.errorDialog.close();
      window.location.reload();
    },
    closeDialog() {
      this.$refs.errorDialog.close();
    },
    decodeUnicode(str) {
      let decoded = str.replace(/\\u([\dA-Fa-f]{4})/g, (match, group) =>
        String.fromCharCode(parseInt(group, 16))
      );
      if (decoded.startsWith('"') && decoded.endsWith('"')) {
        return decoded.slice(1, -1);
      }
      return decoded;
    },
    scrollToLatestMessage() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (container) {
          container.$el.scrollTo({
            top: container.$el.scrollHeight,
            behavior: 'smooth'
          });
        }
      });
    }
  },
  async mounted() {
    await this.getRoomInfo();
  },
  beforeUnmount() {
    if (this.socket) {
      this.socket.close();
    }
  }
};
</script>

<style scoped>
.live-speaker {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
}

.language-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  width: 100%;
  max-width: 450px;
  top: 45%;
  transform: translateY(-50%);
  transition: top 0.5s ease, transform 0.5s ease;
}

@media screen and (max-width: 768px) {
  .language-container {
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
  }
}
</style>